import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
} from '../components/ui/dropdown-menu';
import EmailShareDialog from '../components/EmailShareDialog';
import { QuoteProductSection } from '../components/product-pricing/QuoteProductSection';
import { QuoteDocumentsCard } from '../components/product-pricing/QuoteDocumentsCard';
import SendStakeholdersDialog from '../components/SendStakeholdersDialog';
import ShareLinkDialog from '../components/ShareLinkDialog';
import logger from '../lib/logger';
import {
  Download, Loader2, Briefcase, Clock, DollarSign, Users, BarChart3,
  AlertTriangle, Lightbulb, ChevronDown, FileText, FileSpreadsheet, Mail,
  Building2, Target, Shield, GanttChart as GanttIcon, Printer, Send,
  Check, Eye, EyeOff, MessageSquare, RefreshCw, Bell, CalendarDays,
  GitBranch, Plus, Minus, ArrowRight, Link2, Database, Gauge, Copy, Hash
} from 'lucide-react';

import { useAuth } from '../context/AuthContext';
import { resolveDisplayRounding, isRoundingActive, formatHours, formatCost } from '../lib/rounding';
import { getCurrencySymbol } from '../lib/currency';
import { CurrencyModeToggle, useCurrencyMode } from '../components/insights/CurrencyMode';
import RoundedMetric from '../components/RoundedMetric';

import { CommentBubble, CommentPanel } from '../components/CommentsPanel';
const API_URL = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  { id: 'executive_summary', label: 'Executive Summary' },
  { id: 'cost_breakdown', label: 'Cost & Effort Analysis' },
  { id: 'component_details', label: 'Component Details' },
  { id: 'risk_analysis', label: 'Risk & Feasibility' },
  { id: 'implementation_timeline', label: 'Implementation Timeline' },
];

function getDeadlineStatus(dueDateStr) {
  if (!dueDateStr) return null;
  const now = new Date();
  const due = new Date(dueDateStr);
  const diffMs = due - now;
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return { label: `${Math.abs(diffDays)}d overdue`, color: 'text-red-600', bg: 'bg-red-50 border-red-200', icon: 'overdue' };
  if (diffDays <= 3) return { label: `Due in ${diffDays}d`, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200', icon: 'soon' };
  return { label: `${diffDays}d remaining`, color: 'text-emerald-600', bg: 'bg-emerald-50/50 border-emerald-200', icon: 'ok' };
}

export default function QuotePreviewPage() {
  const { estimateId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [shareLinkOpen, setShareLinkOpen] = useState(false);
  const [approvalData, setApprovalData] = useState(null);
  const [reminding, setReminding] = useState(false);
  const [remindingId, setRemindingId] = useState(null);
  const [expandedTimelines, setExpandedTimelines] = useState(new Set());
  const [estimateAnalytics, setEstimateAnalytics] = useState(null);
  const [versions, setVersions] = useState([]);
  const [versionDiff, setVersionDiff] = useState(null);
  const [showVersions, setShowVersions] = useState(false);
  const [diffLoading, setDiffLoading] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [confirmRestore, setConfirmRestore] = useState(null);
  const [notifying, setNotifying] = useState(false);
  const [showNotifyDialog, setShowNotifyDialog] = useState(false);
  const [notifyMessage, setNotifyMessage] = useState('');
  const [commentCounts, setCommentCounts] = useState({});
  const [activeComment, setActiveComment] = useState(null);
  const [portfolioBenchmark, setPortfolioBenchmark] = useState(null);
  // When checked in the Export dropdown, the backend appends a
  // precise "Raw Values Appendix" to the generated PDF/DOCX/XLSX/PPTX.
  const [includeRawAppendix, setIncludeRawAppendix] = useState(false);
  const [includeBlendedBenchmark, setIncludeBlendedBenchmark] = useState(true);
  const [includeTimelineDelta, setIncludeTimelineDelta] = useState(true);
  // Persist the "Show raw values" toggle per-estimate so stakeholders
  // can flip back and forth during a live review without re-toggling.
  const rawKey = estimateId ? `quote:show-raw:${estimateId}` : null;
  const [showRawValues, setShowRawValues] = useState(() => {
    if (typeof window === 'undefined' || !rawKey) return false;
    return window.sessionStorage.getItem(rawKey) === '1';
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !rawKey) return;
    if (showRawValues) window.sessionStorage.setItem(rawKey, '1');
    else window.sessionStorage.removeItem(rawKey);
  }, [showRawValues, rawKey]);

  // Executive Mode — when ON (default), the page hides the deep-dive sections
  // (Integration scope, Cost & Effort analysis, Project Timeline incl. Gantt,
  // Migration & New-Dev callouts, Risk & Feasibility, Component Appendix) so a
  // stakeholder lands on a tight headline-only view: Project Info → KPIs →
  // Executive Summary → Recommendations → Approval Dashboard. Sales flips it
  // off to deep-dive during a working session. Persisted per-estimate.
  const [ccyMode, setCcyMode] = useCurrencyMode();
  const execKey = estimateId ? `quote:exec-mode:${estimateId}` : null;
  const [executiveMode, setExecutiveMode] = useState(() => {
    if (typeof window === 'undefined' || !execKey) return true;
    const v = window.sessionStorage.getItem(execKey);
    if (v === null) return true;
    return v === '1';
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !execKey) return;
    window.sessionStorage.setItem(execKey, executiveMode ? '1' : '0');
  }, [executiveMode, execKey]);

  // Copy-summary feedback flag — used by the "Copy summary" toolbar button.
  // Resets after ~2.2s. Defined here (not after the early return) to keep
  // hook order stable.
  const [copiedSummary, setCopiedSummary] = useState(false);

  // Fetch approval summary
  const fetchApproval = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/export/quote/${estimateId}/approval-summary`, { withCredentials: true });
      setApprovalData(res.data);
    } catch (err) { logger.error('Failed to fetch approval:', err); }
  }, [estimateId]);

  // Fetch per-estimate analytics
  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/approval-analytics/${estimateId}`, { withCredentials: true });
      setEstimateAnalytics(res.data);
    } catch (err) { logger.error('Failed to fetch analytics:', err); }
  }, [estimateId]);

  // Fetch versions
  const fetchVersions = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/estimates/${estimateId}/versions`, { withCredentials: true });
      setVersions(res.data.versions || []);
    } catch (err) { logger.error('Failed to fetch versions:', err); }
  }, [estimateId]);

  // Fetch comment counts
  const fetchCommentCounts = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/estimates/${estimateId}/comments`, { withCredentials: true });
      setCommentCounts(res.data.section_counts || {});
    } catch (err) { logger.error('Failed to fetch comments:', err); }
  }, [estimateId]);

  // Compare two versions
  const compareDiff = useCallback(async (v1, v2) => {
    setDiffLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/estimates/${estimateId}/versions/diff`, {
        params: { v1, v2 }, withCredentials: true
      });
      setVersionDiff(res.data);
    } catch (err) { logger.error('Failed to diff versions:', err); }
    finally { setDiffLoading(false); }
  }, [estimateId]);

  // Restore version
  const handleRestore = async (versionNum) => {
    setRestoring(versionNum);
    try {
      await axios.post(`${API_URL}/api/estimates/${estimateId}/versions/${versionNum}/restore`, {}, { withCredentials: true });
      await fetchVersions();
      setConfirmRestore(null);
      setVersionDiff(null);
      // Reload the page to get fresh estimate data
      window.location.reload();
    } catch (err) { logger.error('Failed to restore version:', err); }
    finally { setRestoring(null); }
  };

  // Notify stakeholders of revision
  const handleNotifyRevision = async () => {
    setNotifying(true);
    try {
      await axios.post(`${API_URL}/api/estimates/${estimateId}/notify-revision`,
        { message: notifyMessage.trim() || null }, { withCredentials: true });
      setShowNotifyDialog(false);
      setNotifyMessage('');
    } catch (err) { logger.error('Failed to notify:', err); }
    finally { setNotifying(false); }
  };

  useEffect(() => {
    if (estimateId) { fetchApproval(); fetchAnalytics(); fetchVersions(); fetchCommentCounts(); }
  }, [estimateId, fetchApproval, fetchAnalytics, fetchVersions, fetchCommentCounts]);

  const sendReminders = async (recipientIds) => {
    const isAll = !recipientIds;
    isAll ? setReminding(true) : setRemindingId(recipientIds[0]);
    try {
      await axios.post(`${API_URL}/api/export/quote/${estimateId}/send-reminder`,
        { recipient_ids: recipientIds || null }, { withCredentials: true });
      await fetchApproval();
    } catch (err) { logger.error('Failed to send reminders:', err); }
    finally { setReminding(false); setRemindingId(null); }
  };

  // Tracks whether we've already applied the backend-resolved export
  // defaults once — after that we leave the user's in-session toggle alone.
  const rawDefaultAppliedRef = React.useRef(false);
  const benchmarkDefaultAppliedRef = React.useRef(false);
  const timelineDeltaDefaultAppliedRef = React.useRef(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/export/quote-data/${estimateId}`, { withCredentials: true });
        setData(res.data);
        if (res.data?.export_defaults?.include_raw_appendix !== undefined
            && !rawDefaultAppliedRef.current) {
          setIncludeRawAppendix(Boolean(res.data.export_defaults.include_raw_appendix));
          rawDefaultAppliedRef.current = true;
        }
        if (res.data?.export_defaults?.include_blended_benchmark !== undefined
            && !benchmarkDefaultAppliedRef.current) {
          setIncludeBlendedBenchmark(Boolean(res.data.export_defaults.include_blended_benchmark));
          benchmarkDefaultAppliedRef.current = true;
        }
        if (res.data?.export_defaults?.include_timeline_delta !== undefined
            && !timelineDeltaDefaultAppliedRef.current) {
          setIncludeTimelineDelta(Boolean(res.data.export_defaults.include_timeline_delta));
          timelineDeltaDefaultAppliedRef.current = true;
        }
      } catch {
        toast.error('Failed to load quote data');
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };
    if (estimateId) fetchData();
  }, [estimateId, navigate]);

  // Fetch portfolio blended-rate benchmark (excluding self) so we can
  // surface a "vs portfolio average" chip on the Blended Rate tile.
  useEffect(() => {
    if (!estimateId) return;
    let cancelled = false;
    axios
      .get(`${API_URL}/api/insights/blended-rate-benchmark`, {
        params: { exclude_estimate_id: estimateId },
        withCredentials: true,
      })
      .then((res) => { if (!cancelled) setPortfolioBenchmark(res.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [estimateId]);

  const doExport = useCallback(async (format) => {
    setExporting(true);
    setExportMenuOpen(false);
    toast.info(`Exporting ${format.toUpperCase()}...`);
    try {
      const res = await axios.post(`${API_URL}/api/export/quote`, {
        estimate_id: estimateId, format,
        include_raw_appendix: includeRawAppendix,
        include_blended_benchmark: includeBlendedBenchmark,
        include_timeline_delta: includeTimelineDelta,
      }, { withCredentials: true, responseType: 'blob' });
      const mimeMap = { pdf: 'application/pdf', docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' };
      const blob = new Blob([res.data], { type: mimeMap[format] });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url;
      a.download = `Seertech_Quotation_${(data?.estimate?.organization_name || data?.estimate?.estimate_name || 'Quote').replace(/\s/g, '_')}.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch (err) { logger.error('Export failed:', err); toast.error('Export failed'); }
    finally { setExporting(false); }
  }, [estimateId, data, includeRawAppendix, includeBlendedBenchmark, includeTimelineDelta]);

  const generatePdfBase64 = useCallback(async () => {
    try {
      const res = await axios.post(`${API_URL}/api/export/quote`, {
        estimate_id: estimateId, format: 'pdf',
        include_raw_appendix: includeRawAppendix,
        include_blended_benchmark: includeBlendedBenchmark,
        include_timeline_delta: includeTimelineDelta,
      }, { withCredentials: true, responseType: 'blob' });
      return new Promise(resolve => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.readAsDataURL(res.data);
      });
    } catch (err) { logger.error('PDF generation failed:', err); return null; }
  }, [estimateId, includeRawAppendix, includeBlendedBenchmark, includeTimelineDelta]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] gap-3">
        <Loader2 className="w-6 h-6 animate-spin text-[#4ABFCF]" />
        <span className="text-slate-500">Generating Quote preview...</span>
      </div>
    );
  }

  if (!data) return null;

  const { estimate: est, group_breakdown, role_breakdown, components, timeline, narrative, data_integrity: di, new_developments: ndItems, new_dev_overhead: ndOverhead, integrations: estIntegrations, currency_code: effectiveCurrencyCode, export_defaults: exportDefaults, tax_label: taxLabel, project_requirements: projectRequirements, disabled_factor_notes: disabledFactorNotes } = data;
  const totalEffort = est.total_effort || 0;
  const totalCost = est.total_cost || 0;
  const resolvedRounding = resolveDisplayRounding(est, user);
  const enabledIntegrations = (estIntegrations || []).filter(i => i.enabled);
  const currencyCode = effectiveCurrencyCode || est.currency_code || user?.preferences?.default_currency_code || 'AUD';
  const currencySymbol = getCurrencySymbol(currencyCode);
  // Whole-of-quote figures (implementation + subscription) in A$ or the deal's own currencies.
  const pp = data.product_pricing;
  const implFx = currencyCode === 'AUD' ? 1 : (Number(est.fx_rate_to_aud) || null);
  const bookCcy = pp?.currency || 'AUD';
  const bookFx = Number(pp?.fx_to_aud || 1);
  const showAud = ccyMode === 'aud';
  const fmtIn = (v, ccy) => (v == null ? '—' : `${getCurrencySymbol(ccy)}${Math.round(v).toLocaleString()}`);
  const implDisplay = showAud ? (implFx ? fmtIn(totalCost * implFx, 'AUD') : `${currencySymbol}${Math.round(totalCost).toLocaleString()} (${currencyCode})`) : fmtIn(totalCost, currencyCode);
  const arrNative = pp ? Number(pp.years?.[0]?.net || 0) : null;
  const arrDisplay = pp ? (showAud ? fmtIn(arrNative * bookFx, 'AUD') : fmtIn(arrNative, bookCcy)) : null;
  const subTotalNative = pp ? Number(pp.total_net || 0) : 0;
  const tcvCcy = showAud ? 'AUD' : (pp ? bookCcy : currencyCode);
  const implInTcv = showAud ? (implFx ? totalCost * implFx : null) : (pp ? (implFx ? (totalCost * implFx) / bookFx : (currencyCode === bookCcy ? totalCost : null)) : totalCost);
  const tcvValue = implInTcv == null ? null : implInTcv + (showAud ? subTotalNative * bookFx : subTotalNative);
  const blendedDisplay = totalEffort > 0 ? (showAud && implFx ? `${fmtIn((totalCost * implFx) / totalEffort, 'AUD')}/hr` : `${currencySymbol}${Math.round(totalCost / totalEffort).toLocaleString()}/hr`) : '—';

  // ── Copy stakeholder-ready summary to clipboard ──────────────────────────
  // Three formatters share the same data so Sales can paste the canonical
  // numbers into any audience without re-formatting:
  //   slack  — bullets + status emoji, Slack-flavoured single-asterisk bold
  //   email  — polished GitHub-flavoured Markdown (the original v171 format)
  //   plain  — bare text, no Markdown, no emoji
  const buildSummary = (mode = 'email') => {
    const slack = mode === 'slack';
    const plain = mode === 'plain';
    const md = !slack && !plain;

    const heading = (text) => {
      if (md) return `### ${text}`;
      if (slack) return `*${text}*`;
      return text.toUpperCase();
    };
    const bold = (text) => (md ? `**${text}**` : slack ? `*${text}*` : text);
    const italic = (text) => (md ? `_${text}_` : slack ? `_${text}_` : text);
    const bullet = slack ? '• ' : plain ? '- ' : '- ';

    const lines = [];
    // Title
    if (md) lines.push(`**${est.estimate_name || 'Quote Summary'}**`);
    else if (slack) lines.push(`:bookmark_tabs: *${est.estimate_name || 'Quote Summary'}*`);
    else lines.push((est.estimate_name || 'Quote Summary').toUpperCase());
    if (est.organization_name) lines.push(italic(est.organization_name));
    lines.push('');

    // Headline metrics
    lines.push(heading('Headline metrics'));
    const effortStr = `${formatHours(totalEffort, resolvedRounding)} hrs`;
    const costStr = `${formatCost(totalCost, resolvedRounding, currencySymbol)} ${currencyCode}`;
    lines.push(`${bullet}${slack ? ':hourglass_flowing_sand: ' : ''}${bold('Total Effort:')} ${effortStr}`);
    lines.push(`${bullet}${slack ? ':moneybag: ' : ''}${bold('Total Cost:')} ${costStr}`);
    if (timeline?.total_duration_weeks) {
      const dur = `${timeline.total_duration_weeks} weeks${timeline.peak_fte ? ` · Peak FTE: ${timeline.peak_fte.toFixed(1)}` : ''}`;
      lines.push(`${bullet}${slack ? ':calendar: ' : ''}${bold('Duration:')} ${dur}`);
    }
    if (totalEffort > 0) {
      const blended = totalCost / totalEffort;
      let benchmarkLine = '';
      if (portfolioBenchmark?.portfolio_blended_rate && portfolioBenchmark.sample_size >= 3) {
        const port = portfolioBenchmark.portfolio_blended_rate;
        const deltaPct = ((blended - port) / port) * 100;
        if (Math.abs(deltaPct) <= 0.5) {
          benchmarkLine = ` (on par with portfolio avg ${currencySymbol}${Math.round(port).toLocaleString()}/hr)`;
        } else {
          const sign = deltaPct > 0 ? (slack ? ':chart_with_upwards_trend: ' : '↑ ') : (slack ? ':chart_with_downwards_trend: ' : '↓ ');
          const dir = deltaPct > 0 ? 'above' : 'below';
          benchmarkLine = ` (${sign}${Math.abs(deltaPct).toFixed(1)}% ${dir} ${currencySymbol}${Math.round(port).toLocaleString()}/hr avg)`;
        }
      }
      lines.push(`${bullet}${slack ? ':bar_chart: ' : ''}${bold('Blended Rate:')} ${currencySymbol}${Math.round(blended).toLocaleString()}/hr${benchmarkLine}`);
    }

    // Executive Summary
    if (narrative?.executive_summary) {
      lines.push('');
      lines.push(heading('Executive Summary'));
      narrative.executive_summary.split('\n\n').filter(Boolean).forEach(p => lines.push(p.trim()));
    }
    return lines.join('\n');
  };

  const writeToClipboard = async (text) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      return true;
    } catch {
      return false;
    }
  };

  const copyQuoteSummary = async (mode = 'email') => {
    const text = buildSummary(mode);
    const ok = await writeToClipboard(text);
    if (ok) {
      setCopiedSummary(true);
      const label = mode === 'slack' ? 'Slack-formatted' : mode === 'plain' ? 'Plain-text' : 'Markdown';
      toast.success(`${label} summary copied to clipboard`);
      setTimeout(() => setCopiedSummary(false), 2200);
    } else {
      toast.error('Could not copy to clipboard');
    }
  };

  // Source of the currently-applied "Include raw values appendix" default —
  // lets us render an "Applied default · Reset" hint, mirroring the Gantt
  // milestones toggle pattern. Null (= auto, not a real preference) stays
  // silent so the UI isn't noisy for users who never opted in.
  const includeRawDefault = exportDefaults?.include_raw_appendix;
  const includeRawSource = exportDefaults?.include_raw_appendix_source;
  const includeRawOverriding = typeof includeRawDefault === 'boolean'
    && includeRawAppendix !== Boolean(includeRawDefault);
  const resetIncludeRawToDefault = () => {
    if (typeof includeRawDefault === 'boolean') {
      setIncludeRawAppendix(Boolean(includeRawDefault));
    }
  };

  // Small locals honouring the current raw/rounded mode for the deeper
  // breakdown tables (Cost & Effort Analysis + Appendix: Component
  // Breakdown). Keeps rendering one-liner at the call-site.
  const hoursStr = (value, { totals = false } = {}) => {
    const v = Number(value || 0);
    if (showRawValues) {
      return totals
        ? v.toLocaleString(undefined, { maximumFractionDigits: 1 })
        : v.toLocaleString();
    }
    return formatHours(v, resolvedRounding);
  };
  const costStr = (value) => {
    const v = Number(value || 0);
    if (showRawValues) {
      return `${currencySymbol}${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    }
    return formatCost(v, resolvedRounding, currencySymbol);
  };

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-6" data-testid="quote-preview">
      {/* Header bar */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#0F2040] font-['Outfit'] tracking-tight flex items-center gap-2" data-testid="bc-title">
            <Briefcase className="w-5 h-5 text-[#4ABFCF]" /> Quote
          </h1>
          {Object.values(commentCounts).reduce((a, b) => a + b, 0) > 0 && (
            <span className="text-[10px] text-[#4ABFCF] font-medium flex items-center gap-0.5 mt-0.5" data-testid="total-comment-count">
              <MessageSquare className="w-3 h-3" /> {Object.values(commentCounts).reduce((a, b) => a + b, 0)} comment{Object.values(commentCounts).reduce((a, b) => a + b, 0) !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1.5" data-testid="bc-quote-toolbar">
          {/* Output group: PDF/Word/Excel/PPT export + Print. Compact outline
              buttons so they don't compete with the primary CTAs at the
              right edge. UX pass v187 — uniform size="sm" + text-xs. */}
          <div className="relative">
            <Button size="sm" variant="outline"
              onClick={() => setExportMenuOpen(!exportMenuOpen)} disabled={exporting}
              className="text-xs text-slate-700 hover:text-[#0F2040]"
              data-testid="bc-export-btn">
              {exporting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
              {exporting ? 'Exporting…' : 'Export'}
              <ChevronDown className="w-3 h-3 ml-1 opacity-60" />
            </Button>
            {exportMenuOpen && (
              <div className="absolute right-0 top-full mt-1 z-50 bg-white rounded-lg shadow-xl border border-slate-200 py-1 min-w-[240px]" data-testid="bc-export-menu">
                <label
                  className="flex items-start gap-2 px-3 py-2 border-b border-slate-100 cursor-pointer hover:bg-slate-50"
                  data-testid="bc-export-include-raw-row"
                >
                  <input
                    type="checkbox"
                    checked={includeRawAppendix}
                    onChange={(e) => setIncludeRawAppendix(e.target.checked)}
                    className="mt-0.5 accent-[#0F2040] cursor-pointer"
                    data-testid="bc-export-include-raw-checkbox"
                  />
                  <span className="text-xs leading-tight flex-1 min-w-0">
                    <span className="font-semibold text-[#0F2040] block">Include raw values appendix</span>
                    <span className="text-slate-500 text-[11px]">
                      Appends a precise un-rounded table set for audit review.
                    </span>
                    {(includeRawSource === 'user' || includeRawSource === 'org') && (
                      includeRawOverriding ? (
                        <span
                          className="flex items-center gap-1 text-[10px] text-slate-400 mt-1"
                          data-testid="bc-export-include-raw-hint"
                        >
                          <span>Overriding {includeRawSource === 'user' ? 'your' : 'org'} default</span>
                          <span>·</span>
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); resetIncludeRawToDefault(); }}
                            className="text-[#4ABFCF] hover:text-[#0F2040] font-medium underline underline-offset-2 decoration-dotted"
                            data-testid="bc-export-include-raw-reset"
                          >
                            Reset
                          </button>
                        </span>
                      ) : (
                        <span
                          className="flex items-center gap-1 text-[10px] text-slate-400 mt-1"
                          data-testid="bc-export-include-raw-hint"
                        >
                          <span>
                            Applied default: {includeRawDefault ? 'Checked' : 'Unchecked'} ·
                          </span>
                          {includeRawSource === 'user' ? (
                            <Link
                              to="/profile"
                              onClick={(e) => e.stopPropagation()}
                              className="text-[#4ABFCF] hover:text-[#0F2040] font-medium underline underline-offset-2 decoration-dotted"
                              title="Open Profile → Export Defaults"
                            >
                              your profile
                            </Link>
                          ) : (
                            <span className="text-slate-500 font-medium">org default</span>
                          )}
                        </span>
                      )
                    )}
                  </span>
                </label>
                <label
                  className="flex items-start gap-2 px-3 py-2 border-b border-slate-100 cursor-pointer hover:bg-slate-50"
                  data-testid="bc-export-include-benchmark-row"
                >
                  <input
                    type="checkbox"
                    checked={includeBlendedBenchmark}
                    onChange={(e) => setIncludeBlendedBenchmark(e.target.checked)}
                    className="mt-0.5 accent-[#0F2040] cursor-pointer"
                    data-testid="bc-export-include-benchmark-checkbox"
                  />
                  <span className="text-xs leading-tight flex-1 min-w-0">
                    <span className="font-semibold text-[#0F2040] block">Include benchmark callout</span>
                    <span className="text-slate-500 text-[11px]">
                      Stamps a "vs portfolio average" line under Blended Rate. Hide for sensitive deals.
                    </span>
                  </span>
                </label>
                {data?.estimate?.results?.timeline_stretch && (
                  <label
                    className="flex items-start gap-2 px-3 py-2 border-b border-slate-100 cursor-pointer hover:bg-slate-50"
                    data-testid="bc-export-include-timeline-delta-row"
                  >
                    <input
                      type="checkbox"
                      checked={includeTimelineDelta}
                      onChange={(e) => setIncludeTimelineDelta(e.target.checked)}
                      className="mt-0.5 accent-[#0F2040] cursor-pointer"
                      data-testid="bc-export-include-timeline-delta-checkbox"
                    />
                    <span className="text-xs leading-tight flex-1 min-w-0">
                      <span className="font-semibold text-[#0F2040] block">Include timeline delta callout</span>
                      <span className="text-slate-500 text-[11px]">
                        Stamps "Auto-projected vs Planner-committed" under cover-page metrics. Hide for fixed-fee deals.
                      </span>
                    </span>
                  </label>
                )}
                <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wide text-slate-400 font-semibold" data-testid="bc-export-heading">Seertech Product &amp; Service Quotation</p>
                {[['pdf', 'PDF quotation', FileText], ['docx', 'Word quotation', FileText], ['xlsx', 'Excel quotation', FileSpreadsheet]].map(([fmt, label, Icon]) => (
                  <button key={fmt} onClick={() => doExport(fmt)}
                    className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2" data-testid={`bc-export-${fmt}`}>
                    <Icon className="w-3.5 h-3.5 text-slate-400" /> {label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <Button size="sm" variant="outline"
            onClick={() => window.open(`/estimator/quote/${estimateId}/print`, '_blank', 'noopener,noreferrer')}
            className="text-xs text-slate-700 hover:text-[#0F2040]" data-testid="bc-print-btn">
            <Printer className="w-3.5 h-3.5 mr-1.5" /> Print
          </Button>

          {/* Visual divider between Output and Share groups */}
          <span className="h-5 w-px bg-slate-200 mx-1 hidden sm:inline" aria-hidden />

          {/* Share group: Email · Share Link · Copy summary */}
          <EmailShareDialog
            onGeneratePdf={generatePdfBase64}
            defaultSubject={`Quote — ${est.estimate_name}`}
            defaultFilename={`Quote_${(est.estimate_name || 'Report').replace(/\s/g, '_')}.pdf`}
            triggerClassName="text-xs h-8 px-3"
          />
          <Button size="sm" variant="outline"
            onClick={() => setShareLinkOpen(true)}
            className="text-xs text-slate-700 hover:text-[#0F2040]"
            data-testid="bc-share-link-btn">
            <Link2 className="w-3.5 h-3.5 mr-1.5" /> Share Link
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline"
                className="text-xs text-slate-700 hover:text-[#0F2040]"
                data-testid="bc-copy-summary-btn"
                title="Copy a stakeholder-ready summary to your clipboard. Pick a format below.">
                {copiedSummary ? <Check className="w-3.5 h-3.5 mr-1.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5 mr-1.5" />}
                {copiedSummary ? 'Copied!' : 'Copy summary'}
                <ChevronDown className="w-3 h-3 ml-1 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-slate-400">Pick a format</DropdownMenuLabel>
              <DropdownMenuItem
                onSelect={() => copyQuoteSummary('email')}
                data-testid="bc-copy-summary-email"
              >
                <Mail className="w-3.5 h-3.5 mr-2 text-cyan-600" />
                <div className="flex flex-col">
                  <span className="text-xs font-medium">Email (Markdown)</span>
                  <span className="text-[10px] text-slate-400">Polished headings + bullets</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => copyQuoteSummary('slack')}
                data-testid="bc-copy-summary-slack"
              >
                <Hash className="w-3.5 h-3.5 mr-2 text-purple-600" />
                <div className="flex flex-col">
                  <span className="text-xs font-medium">Slack (text + emoji)</span>
                  <span className="text-[10px] text-slate-400">📊 emoji, single-asterisk bold</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => copyQuoteSummary('plain')}
                data-testid="bc-copy-summary-plain"
              >
                <FileText className="w-3.5 h-3.5 mr-2 text-slate-600" />
                <div className="flex flex-col">
                  <span className="text-xs font-medium">Plain text</span>
                  <span className="text-[10px] text-slate-400">No formatting, no emoji</span>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Visual divider before primary CTAs */}
          <span className="h-5 w-px bg-slate-200 mx-1 hidden sm:inline" aria-hidden />

          {/* Primary group: Send to Stakeholders (cyan) + Generate SOW (navy) */}
          <Button size="sm" onClick={() => setSendDialogOpen(true)}
            className="text-xs bg-[#4ABFCF] hover:bg-[#3AAFBF] text-white" data-testid="bc-send-stakeholders-btn">
            <Send className="w-3.5 h-3.5 mr-1.5" /> Send to Stakeholders
          </Button>
          {['admin', 'cs', 'cro', 'cco', 'super_admin'].includes(user?.role) && (
            <Button size="sm" onClick={() => navigate(`/sow/new?estimate=${estimateId}`)}
              className="text-xs bg-[#0F2040] hover:bg-[#1A3055] text-white" data-testid="generate-sow-btn">
              <FileText className="w-3.5 h-3.5 mr-1.5" /> Generate SOW
            </Button>
          )}
        </div>
      </div>

      {/* Version History */}
      {versions.length > 0 && (
        <Card className="border-slate-200" data-testid="version-history-card">
          <CardHeader className="py-2.5 px-4 cursor-pointer" onClick={() => setShowVersions(!showVersions)}>
            <CardTitle className="text-xs font-semibold text-[#0F2040] flex items-center justify-between">
              <span className="flex items-center gap-1.5">
                <GitBranch className="w-3.5 h-3.5 text-[#4ABFCF]" />
                Version History
                <Badge variant="outline" className="text-[8px] ml-1">{versions.length} version{versions.length > 1 ? 's' : ''}</Badge>
              </span>
              <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${showVersions ? 'rotate-180' : ''}`} />
            </CardTitle>
          </CardHeader>
          {showVersions && (
            <CardContent className="pt-0 px-4 pb-4">
              {/* Notify Stakeholders button */}
              {versions.length > 1 && (
                <div className="mb-3 flex items-center justify-end">
                  <button onClick={() => setShowNotifyDialog(true)}
                    className="text-[10px] text-[#0F2040] hover:text-[#4ABFCF] font-semibold flex items-center gap-1 transition-colors bg-slate-100 hover:bg-slate-50 px-2.5 py-1.5 rounded-lg border border-slate-200"
                    data-testid="notify-revision-btn">
                    <Send className="w-3 h-3" /> Notify Stakeholders of Revision
                  </button>
                </div>
              )}

              {/* Notify Dialog */}
              {showNotifyDialog && (
                <div className="mb-3 bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2" data-testid="notify-dialog">
                  <p className="text-xs font-semibold text-[#0F2040]">Send revision notification to all stakeholders</p>
                  <textarea value={notifyMessage} onChange={e => setNotifyMessage(e.target.value)}
                    rows={2} placeholder="Optional message about what changed..."
                    className="w-full border rounded-md px-2.5 py-1.5 text-xs focus:ring-1 focus:ring-[#4ABFCF] focus:border-[#4ABFCF] resize-y"
                    data-testid="notify-message" />
                  <div className="flex items-center gap-2 justify-end">
                    <button onClick={() => setShowNotifyDialog(false)} className="text-[10px] text-slate-400 hover:text-slate-600">Cancel</button>
                    <button onClick={handleNotifyRevision} disabled={notifying}
                      className="text-[10px] bg-[#0F2040] text-white px-3 py-1.5 rounded-lg font-semibold hover:bg-[#1A3055] disabled:opacity-50 flex items-center gap-1"
                      data-testid="notify-send-btn">
                      {notifying ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                      {notifying ? 'Sending...' : 'Send Notification'}
                    </button>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                {versions.map((v, i) => (
                  <div key={v.version_number} className={`flex items-start gap-3 px-3 py-2 rounded-lg border transition-colors ${
                    i === 0 ? 'bg-[#4ABFCF]/5 border-[#4ABFCF]/30' : 'bg-slate-50 border-slate-200'
                  }`} data-testid={`version-entry-${v.version_number}`}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-[10px] font-bold ${
                      i === 0 ? 'bg-[#4ABFCF] text-white' : 'bg-slate-200 text-slate-500'
                    }`}>v{v.version_number}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-[#0F2040]">{v.change_summary}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        {v.created_by} &middot; {new Date(v.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 mt-0.5">
                      {i > 0 && (
                        <>
                          {confirmRestore === v.version_number ? (
                            <div className="flex items-center gap-1">
                              <button onClick={() => handleRestore(v.version_number)} disabled={restoring === v.version_number}
                                className="text-[9px] bg-emerald-600 text-white px-2 py-0.5 rounded font-semibold hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-0.5"
                                data-testid={`confirm-restore-${v.version_number}`}>
                                {restoring === v.version_number ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Check className="w-2.5 h-2.5" />}
                                Confirm
                              </button>
                              <button onClick={() => setConfirmRestore(null)} className="text-[9px] text-slate-400 hover:text-red-500">
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button onClick={() => setConfirmRestore(v.version_number)}
                              className="text-[9px] text-amber-600 hover:text-amber-700 font-medium flex items-center gap-0.5"
                              data-testid={`restore-btn-${v.version_number}`}>
                              <RefreshCw className="w-2.5 h-2.5" /> Restore
                            </button>
                          )}
                        </>
                      )}
                      {i < versions.length - 1 && (
                        <button onClick={() => compareDiff(versions[i + 1].version_number, v.version_number)}
                          className="text-[9px] text-[#4ABFCF] hover:text-[#3AAFBF] font-medium flex items-center gap-0.5"
                          data-testid={`compare-btn-${v.version_number}`}>
                          Compare <ArrowRight className="w-2.5 h-2.5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Diff View */}
              {diffLoading && (
                <div className="mt-4 text-center py-4"><Loader2 className="w-5 h-5 text-[#4ABFCF] animate-spin mx-auto" /></div>
              )}
              {versionDiff && !diffLoading && (
                <div className="mt-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="version-diff-view">
                  <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex items-center justify-between">
                    <span className="text-xs font-semibold text-[#0F2040]">
                      Changes: v{versionDiff.v1} → v{versionDiff.v2}
                    </span>
                    {versionDiff.cost.delta !== 0 && (
                      <Badge className={`text-[9px] ${versionDiff.cost.delta > 0 ? 'bg-red-50 text-red-600 border-red-200' : 'bg-emerald-50 text-emerald-600 border-emerald-200'}`}>
                        {versionDiff.cost.delta > 0 ? '+' : ''}${versionDiff.cost.delta.toLocaleString()}
                      </Badge>
                    )}
                    <button onClick={() => setVersionDiff(null)} className="text-[9px] text-slate-400 hover:text-red-500">Close</button>
                  </div>
                  <div className="p-3 space-y-2.5 text-xs">
                    {/* Config changes */}
                    {Object.entries(versionDiff.config_changes).length > 0 && (
                      <div>
                        <p className="text-[10px] font-semibold text-slate-500 uppercase mb-1">Configuration</p>
                        {Object.entries(versionDiff.config_changes).map(([field, val]) => (
                          <div key={field} className="flex items-center gap-2 py-1 border-b border-slate-100 last:border-0">
                            <span className="text-slate-400 w-28 shrink-0">{field.replace(/_/g, ' ')}</span>
                            <span className="text-red-500 line-through">{String(val.old || '—')}</span>
                            <ArrowRight className="w-3 h-3 text-slate-300 shrink-0" />
                            <span className="text-emerald-600 font-semibold">{String(val.new || '—')}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Added components */}
                    {versionDiff.components.added.length > 0 && (
                      <div>
                        <p className="text-[10px] font-semibold text-emerald-600 uppercase mb-1 flex items-center gap-1"><Plus className="w-3 h-3" /> Added ({versionDiff.components.added.length})</p>
                        {versionDiff.components.added.map(c => (
                          <div key={c.component_name} className="bg-emerald-50 border border-emerald-200 rounded px-2.5 py-1.5 mb-1 text-emerald-700">
                            {c.component_name} (qty: {c.quantity})
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Removed components */}
                    {versionDiff.components.removed.length > 0 && (
                      <div>
                        <p className="text-[10px] font-semibold text-red-600 uppercase mb-1 flex items-center gap-1"><Minus className="w-3 h-3" /> Removed ({versionDiff.components.removed.length})</p>
                        {versionDiff.components.removed.map(c => (
                          <div key={c.component_name} className="bg-red-50 border border-red-200 rounded px-2.5 py-1.5 mb-1 text-red-700 line-through">
                            {c.component_name} (qty: {c.quantity})
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Changed components */}
                    {versionDiff.components.changed.length > 0 && (
                      <div>
                        <p className="text-[10px] font-semibold text-amber-600 uppercase mb-1">Modified ({versionDiff.components.changed.length})</p>
                        {versionDiff.components.changed.map(c => (
                          <div key={c.component_name} className="bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5 mb-1">
                            <span className="font-semibold text-[#0F2040]">{c.component_name}</span>
                            <div className="mt-1 flex flex-wrap gap-2">
                              {Object.entries(c.changes).map(([f, val]) => (
                                <span key={f} className="text-[10px]">
                                  <span className="text-slate-400">{f}:</span>{' '}
                                  <span className="text-red-500">{String(val.old ?? '—')}</span>{' → '}
                                  <span className="text-emerald-600 font-semibold">{String(val.new ?? '—')}</span>
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Cost summary */}
                    {(versionDiff.cost.old > 0 || versionDiff.cost.new > 0) && (
                      <div className="bg-slate-50 rounded-lg px-3 py-2 flex items-center justify-between">
                        <span className="text-slate-500">Total Cost</span>
                        <span>
                          <span className="text-slate-400">${versionDiff.cost.old.toLocaleString()}</span>
                          <ArrowRight className="w-3 h-3 inline mx-1 text-slate-300" />
                          <span className="font-bold text-[#0F2040]">${versionDiff.cost.new.toLocaleString()}</span>
                        </span>
                      </div>
                    )}

                    {/* No changes */}
                    {versionDiff.components.added.length === 0 && versionDiff.components.removed.length === 0 &&
                     versionDiff.components.changed.length === 0 && Object.keys(versionDiff.config_changes).length === 0 && (
                      <p className="text-slate-400 text-center py-3">No structural changes detected</p>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          )}
        </Card>
      )}

      {/* Document body */}
      <div className="space-y-6">
        {/* Project Details Card — UX pass v187:
            Hero row: Project name spanning full width with the description
              tucked underneath (no more cramming into a 25% column).
            Stat strip: 5 evenly-spaced fields below — uses the full row,
              eliminates the "lots of whitespace at right" issue. */}
        <Card className="border-slate-200" data-testid="bc-project-details">
          <CardContent className="p-6 space-y-4">
            {/* Hero — Project name + optional description */}
            <div className="space-y-1">
              <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Project</span>
              <h2 className="text-lg sm:text-xl font-bold text-[#0F2040] leading-snug font-['Outfit'] break-words" data-testid="bc-project-name">
                {est.estimate_name}
              </h2>
              {est.description && (
                <p className="text-xs text-slate-600 max-w-3xl">{est.description}</p>
              )}
            </div>

            {/* Stat strip — 5 fields, evenly distributed, divided */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-3 pt-3 border-t border-slate-100">
              <div className="min-w-0">
                <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold block">Organization</span>
                <span className="text-sm font-semibold text-[#0F2040] truncate block" title={est.organization_name || ''}>{est.organization_name || '—'}</span>
              </div>
              <div className="min-w-0">
                <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold block">Type</span>
                <span className="text-sm font-semibold text-[#0F2040] capitalize truncate block">{(est.implementation_type || '—').replace(/_/g, ' ')}</span>
              </div>
              <div className="min-w-0">
                <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold block">Hosting</span>
                <span className="text-sm font-semibold text-[#0F2040] capitalize truncate block">{(est.hosting_model || '—').replace(/_/g, ' ')}</span>
              </div>
              <div className="min-w-0">
                <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold block">Complexity</span>
                <Badge variant="outline" className="text-xs capitalize mt-0.5">{est.complexity || '—'}</Badge>
              </div>
              <div className="min-w-0">
                <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold block">Audience</span>
                <span className="text-sm font-semibold text-[#0F2040] truncate block">{est.target_audience ? `${Number(est.target_audience).toLocaleString()} users` : '—'}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Display toggles — Executive Mode is always available; Show-raw only when rounding is active */}
        <div className="flex items-center justify-end gap-4 -mb-1 flex-wrap" data-testid="quote-display-toggles">
          <CurrencyModeToggle mode={ccyMode} onChange={setCcyMode} testId="quote-ccy-mode" />
          <div
            className="flex items-center gap-2"
            data-testid="quote-executive-mode-toggle"
            title={executiveMode
              ? 'Executive view: KPIs + Summary + Recommendations + Approvals only.'
              : 'Full detail: includes Integration scope, Cost & Effort analysis, Timeline, Risk register, Component appendix.'}
          >
            <span className={`text-xs font-medium ${executiveMode ? 'text-[#0F2040]' : 'text-slate-400'}`}>
              Executive
            </span>
            <Switch
              checked={!executiveMode}
              onCheckedChange={(v) => setExecutiveMode(!v)}
              data-testid="quote-executive-mode-switch"
              aria-label="Toggle Executive Mode"
            />
            <span className={`text-xs font-medium ${executiveMode ? 'text-slate-400' : 'text-[#0F2040]'}`}>
              Full detail
            </span>
          </div>

          {isRoundingActive(resolvedRounding) && (
            <div
              className="flex items-center gap-2"
              data-testid="quote-values-display-toggle"
            >
              <span
                className={`text-xs font-medium ${showRawValues ? 'text-slate-400' : 'text-[#0F2040]'}`}
              >
                Rounded
              </span>
              <Switch
                checked={showRawValues}
                onCheckedChange={setShowRawValues}
                data-testid="quote-show-raw-switch"
                aria-label="Show raw values"
              />
              <span
                className={`text-xs font-medium ${showRawValues ? 'text-[#0F2040]' : 'text-slate-400'}`}
              >
                Show raw values
              </span>
            </div>
          )}
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="bc-metrics">
          {[
            {
              label: showRawValues ? 'Total Effort (raw)' : 'Total Effort',
              node: (
                <RoundedMetric
                  kind="hours"
                  value={totalEffort}
                  rounding={resolvedRounding}
                  estimate={est}
                  user={user}
                  showRaw={showRawValues}
                  hideBadge
                  testId="bc-metric-total-effort"
                />
              ),
              icon: Clock,
              color: 'text-blue-600',
              bg: 'bg-blue-50',
            },
            {
              label: showRawValues ? 'Implementation (raw)' : 'Implementation',
              node: showAud && currencyCode !== 'AUD' ? <span data-testid="bc-metric-total-cost-aud">{implDisplay}</span> : (
                <RoundedMetric
                  kind="cost"
                  value={totalCost}
                  rounding={resolvedRounding}
                  estimate={est}
                  user={user}
                  currencyCode={currencyCode}
                  symbol={currencySymbol}
                  showRaw={showRawValues}
                  hideBadge
                  testId="bc-metric-total-cost"
                />
              ),
              icon: DollarSign,
              color: 'text-emerald-600',
              bg: 'bg-emerald-50',
              subNode: taxLabel?.applied ? (
                <span
                  className="text-[10px] text-slate-500 uppercase tracking-wide font-medium"
                  data-testid="quote-tax-label"
                >
                  {taxLabel.applied}
                </span>
              ) : null,
            },
            {
              label: 'Product subscription (ARR)',
              value: arrDisplay || '—',
              icon: DollarSign,
              color: 'text-indigo-600',
              bg: 'bg-indigo-50',
              testId: 'bc-metric-arr',
              subNode: pp ? <span className="text-[10px] text-slate-500 uppercase tracking-wide font-medium" data-testid="bc-metric-arr-sub">{pp.package} · {pp.term_years}-yr · term {showAud ? fmtIn(subTotalNative * bookFx, 'AUD') : fmtIn(subTotalNative, bookCcy)}</span>
                : <span className="text-[10px] text-slate-400 uppercase tracking-wide font-medium">No subscription priced</span>,
            },
            {
              label: 'Total contract value',
              value: tcvValue == null ? '—' : fmtIn(tcvValue, tcvCcy),
              icon: DollarSign,
              color: 'text-[#0F2040]',
              bg: 'bg-[#4ABFCF]/15',
              testId: 'bc-metric-tcv',
              subNode: <span className="text-[10px] text-slate-500 uppercase tracking-wide font-medium" data-testid="bc-metric-tcv-sub">{pp ? `Implementation + ${pp.term_years}-yr subscription` : 'Implementation only'}{tcvValue == null ? ' · FX not saved' : ` · ${tcvCcy}`}</span>,
            },
            {
              label: 'Blended Rate',
              value: blendedDisplay,
              icon: Gauge,
              color: 'text-cyan-600',
              bg: 'bg-cyan-50',
              testId: 'bc-metric-blended-rate',
              subNode: (
                <BlendedRateBenchmarkChip
                  estimateBlended={totalEffort > 0 ? totalCost / totalEffort : 0}
                  benchmark={portfolioBenchmark}
                  currencySymbol={currencySymbol}
                />
              ),
            },
            { label: 'Components', value: est.component_count, icon: BarChart3, color: 'text-violet-600', bg: 'bg-violet-50', testId: 'bc-metric-components' },
            { label: 'Duration', value: timeline ? `${timeline.total_duration_weeks} wks` : '—', icon: GanttIcon, color: 'text-amber-600', bg: 'bg-amber-50', testId: 'bc-metric-duration' },
            { label: 'Peak FTE', value: timeline ? timeline.peak_fte.toFixed(1) : '—', icon: Users, color: 'text-rose-600', bg: 'bg-rose-50', testId: 'bc-metric-peak-fte' },
          ].map((m) => (
            <Card key={`metric-${m.label}`} className="border-slate-200 hover:shadow-sm transition-shadow" data-testid={m.testId}>
              <CardContent className="p-4 flex flex-col items-stretch text-center">
                {/* Top row: icon pill + label — fixed-height so the value
                    row below sits at the same Y on every card. */}
                <div className="flex items-center gap-2 self-stretch h-7 mb-3">
                  <div className={`w-7 h-7 rounded-lg ${m.bg} flex items-center justify-center shrink-0`}>
                    <m.icon className={`w-3.5 h-3.5 ${m.color}`} />
                  </div>
                  <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold truncate">{m.label}</span>
                </div>
                {/* Value row — fixed height + top-anchored, so the first
                    line of every value sits at the same Y on every card,
                    even when the cost value wraps to a second line. */}
                <div className="flex items-start justify-center h-16 w-full">
                  <p className="text-xl sm:text-2xl font-bold text-[#0F2040] font-['Outfit'] leading-tight break-words">
                    {m.node || m.value}
                  </p>
                </div>
                {/* Footnote slot — always reserved (fixed min-height) so
                    cards without a footnote still leave the value anchored
                    where the footnoted cards anchor theirs. */}
                <div className="mt-2 pt-2 border-t border-slate-100 w-full text-center min-h-[36px] flex items-center justify-center">
                  {m.subNode || <span className="text-[10px] text-transparent select-none">—</span>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <QuoteDocumentsCard estimateId={estimateId} />

        {/* Executive Summary */}
        {narrative?.executive_summary && (
          <Card className="border-[#4ABFCF]/20 bg-gradient-to-br from-cyan-50/30 to-white" data-testid="bc-executive-summary">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold text-[#0F2040] flex items-center gap-2">
                <Target className="w-4 h-4 text-[#4ABFCF]" /> Executive Summary
                <CommentBubble count={commentCounts.executive_summary} onClick={setActiveComment} section="executive_summary" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-sm text-slate-700 leading-relaxed space-y-3">
                {narrative.executive_summary.split('\n\n').filter(Boolean).map((p, i) => <p key={`para-${i}`}>{p}</p>)}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Project Requirements (Project estimates — mirrors the Project SOW) */}
        {((projectRequirements || []).length > 0 || (disabledFactorNotes || []).length > 0) && (
          <Card className="border-amber-200 bg-gradient-to-br from-amber-50/40 to-white" data-testid="bc-project-requirements">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold text-[#0F2040] flex items-center gap-2">
                <FileText className="w-4 h-4 text-amber-600" /> Project Requirements
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {projectRequirements.map((sec) => (
                <div key={sec.title} data-testid={`bc-project-req-${sec.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                  <h4 className="text-sm font-semibold text-amber-800 mb-1">{sec.title}</h4>
                  <div className="text-sm text-slate-700 leading-relaxed prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-li:my-0">
                    <ReactMarkdown>{sec.markdown}</ReactMarkdown>
                  </div>
                </div>
              ))}
              {(disabledFactorNotes || []).length > 0 && (
                <div data-testid="bc-disabled-factors">
                  <h4 className="text-sm font-semibold text-amber-800 mb-1">Project factors excluded from this estimate</h4>
                  <ul className="text-sm text-slate-700 space-y-0.5 list-disc pl-5">
                    {disabledFactorNotes.map(f => (
                      <li key={f.label}>
                        <span className="font-medium">{f.label}</span>
                        {' — '}
                        <span className={f.note ? '' : 'text-slate-400 italic'}>
                          {f.note || 'not applicable to this project (counted at its optimal value)'}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Integration Scope */}
        {!executiveMode && enabledIntegrations.length > 0 && (
          <Card className="border-slate-200" data-testid="bc-integration-scope">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold text-[#0F2040] flex items-center gap-2">
                <Shield className="w-4 h-4 text-indigo-500" /> Integration Scope
                <Badge variant="outline" className="text-[10px] ml-1">{enabledIntegrations.length} integration{enabledIntegrations.length !== 1 ? 's' : ''}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {enabledIntegrations.map((intg, i) => (
                  <div key={`intg-${intg.type || i}`} className="flex items-center gap-2 p-2.5 bg-indigo-50/50 rounded-md border border-indigo-100">
                    <Check className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                    <div>
                      <span className="text-sm font-medium text-slate-700">{intg.label}</span>
                      {intg.notes && <p className="text-[10px] text-slate-400">{intg.notes}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {data.product_pricing && (
          <QuoteProductSection product={data.product_pricing} implementationAud={est?.fx_rate_to_aud ? (est.total_cost || 0) * est.fx_rate_to_aud : (effectiveCurrencyCode === 'AUD' ? est?.total_cost : 0)} />
        )}

        {/* Cost & Effort Analysis */}
        {!executiveMode && (
        <Card data-testid="bc-cost-effort">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-[#0F2040] flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-emerald-500" /> Cost & Effort Analysis
                <CommentBubble count={commentCounts.cost_breakdown} onClick={setActiveComment} section="cost_breakdown" />
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Group breakdown table */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">By Component Group</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="bc-group-table">
                  <thead><tr className="bg-[#0F2040] text-white text-xs">
                    <th className="text-left px-3 py-2 font-medium">Group</th><th className="text-left px-3 py-2 font-medium">Components</th>
                    <th className="text-left px-3 py-2 font-medium">Effort</th><th className="text-left px-3 py-2 font-medium">Cost</th>
                    <th className="text-left px-3 py-2 font-medium">% Total</th><th className="px-3 py-2 font-medium w-40"></th>
                  </tr></thead>
                  <tbody>
                    {group_breakdown.map((g) => (
                      <tr key={`grprow-${g.name}`} className="bg-white odd:bg-slate-50">
                        <td className="px-3 py-2 font-medium text-[#0F2040]">{g.name}</td>
                        <td className="px-3 py-2">{g.count}</td>
                        <td className="px-3 py-2">{hoursStr(g.effort)}h</td>
                        <td className="px-3 py-2">{costStr(g.cost)}</td>
                        <td className="px-3 py-2">{g.pct}%</td>
                        <td className="px-3 py-2"><div className="w-full bg-slate-100 rounded-full h-2"><div className="h-2 rounded-full bg-[#4ABFCF]" style={{ width: `${Math.min(g.pct, 100)}%` }} /></div></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-[#0F2040] bg-slate-50 font-semibold text-[#0F2040]">
                      <td className="px-3 py-2">Total</td>
                      <td className="px-3 py-2">{group_breakdown.reduce((s, g) => s + g.count, 0)}</td>
                      <td className="px-3 py-2">{hoursStr(group_breakdown.reduce((s, g) => s + g.effort, 0), { totals: true })}h</td>
                      <td className="px-3 py-2">{costStr(group_breakdown.reduce((s, g) => s + g.cost, 0))}</td>
                      <td className="px-3 py-2">100%</td>
                      <td className="px-3 py-2"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Role breakdown */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">By Role</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="bc-role-table">
                  <thead><tr className="bg-[#0F2040] text-white text-xs">
                    <th className="text-left px-3 py-2 font-medium">Role</th><th className="text-left px-3 py-2 font-medium">Hours</th>
                    <th className="text-left px-3 py-2 font-medium">Rate</th><th className="text-left px-3 py-2 font-medium">Cost</th>
                    <th className="text-left px-3 py-2 font-medium">% Total</th>
                  </tr></thead>
                  <tbody>
                    {role_breakdown.map((r) => (
                      <tr key={`role-${r.role_label}`} className="bg-white odd:bg-slate-50">
                        <td className="px-3 py-2 font-medium text-[#0F2040]">{r.role_label}</td>
                        <td className="px-3 py-2">{hoursStr(r.hours)}h</td>
                        <td className="px-3 py-2">${r.rate}/hr</td>
                        <td className="px-3 py-2">{costStr(r.cost)}</td>
                        <td className="px-3 py-2">{r.pct}%</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-[#0F2040] bg-slate-50 font-semibold text-[#0F2040]">
                      <td className="px-3 py-2">Total</td>
                      <td className="px-3 py-2">{hoursStr(role_breakdown.reduce((s, r) => s + r.hours, 0), { totals: true })}h</td>
                      <td className="px-3 py-2"></td>
                      <td className="px-3 py-2">{costStr(role_breakdown.reduce((s, r) => s + r.cost, 0))}</td>
                      <td className="px-3 py-2">100%</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </CardContent>
        </Card>
        )}

        {/* Project Timeline */}
        {!executiveMode && timeline && (
          <Card data-testid="bc-timeline">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold text-[#0F2040] flex items-center gap-2">
                <GanttIcon className="w-4 h-4 text-amber-500" /> Project Timeline
                <CommentBubble count={commentCounts.implementation_timeline} onClick={setActiveComment} section="implementation_timeline" />
              </CardTitle>
              <p className="text-xs text-slate-500 mt-1">
                Duration: <strong>{timeline.total_duration_weeks} weeks</strong> &middot; Effort: <strong>{timeline.total_effort_hours.toLocaleString()}h</strong> &middot; Peak FTE: <strong>{timeline.peak_fte}</strong>
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Phase table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="bc-phase-table">
                  <thead><tr className="bg-[#0F2040] text-white text-xs">
                    <th className="text-left px-3 py-2 font-medium">Phase</th><th className="text-left px-3 py-2 font-medium">Start</th>
                    <th className="text-left px-3 py-2 font-medium">End</th><th className="text-left px-3 py-2 font-medium">Duration</th>
                    <th className="text-left px-3 py-2 font-medium">Effort</th><th className="px-3 py-2 font-medium w-48">Timeline</th>
                  </tr></thead>
                  <tbody>
                    {timeline.phases.map((p, i) => {
                      const maxWk = timeline.total_duration_weeks || 1;
                      const left = ((p.start_week - 1) / maxWk) * 100;
                      const width = (p.duration_weeks / maxWk) * 100;
                      return (
                        <tr key={`phase-${p.name}`} className="bg-white odd:bg-slate-50">
                          <td className="px-3 py-2 font-medium text-[#0F2040]">{p.name}</td>
                          <td className="px-3 py-2">Wk {p.start_week}</td>
                          <td className="px-3 py-2">Wk {p.end_week}</td>
                          <td className="px-3 py-2">{p.duration_weeks} wk</td>
                          <td className="px-3 py-2">{p.effort_hours.toLocaleString()}h</td>
                          <td className="px-3 py-2">
                            <div className="w-full bg-slate-100 rounded h-3 relative">
                              <div className="absolute h-3 rounded bg-[#4ABFCF]/70" style={{ left: `${left}%`, width: `${Math.max(width, 3)}%` }} />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-[#0F2040] bg-slate-50 font-semibold text-[#0F2040]">
                      <td className="px-3 py-2">Total</td>
                      <td className="px-3 py-2">Wk 1</td>
                      <td className="px-3 py-2">Wk {timeline.total_duration_weeks}</td>
                      <td className="px-3 py-2">{timeline.total_duration_weeks} wk</td>
                      <td className="px-3 py-2">{timeline.phases.reduce((s, p) => s + p.effort_hours, 0).toLocaleString()}h</td>
                      <td className="px-3 py-2"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Expanded Gantt Chart */}
              <div data-testid="bc-gantt-chart">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Project Gantt Chart</p>
                <div className="border border-slate-200 rounded-lg p-4 bg-white">
                  {/* Week headers */}
                  <div className="flex mb-1">
                    <div className="w-40 shrink-0" />
                    <div className="flex-1 relative overflow-hidden" style={{ height: 16 }}>
                      {(() => {
                        const totalWks = timeline.total_duration_weeks || 1;
                        // Show at most ~15 labels; pick a nice step size
                        const step = totalWks <= 15 ? 1 : totalWks <= 30 ? 2 : totalWks <= 60 ? 5 : 10;
                        const labels = [];
                        for (let w = 1; w <= totalWks; w += step) {
                          labels.push(w);
                        }
                        return labels.map((w) => (
                          <div
                            key={`wk-${w}`}
                            className="text-center text-[10px] text-slate-400 font-medium"
                            style={{ position: 'absolute', left: `${((w - 1) / totalWks) * 100}%`, transform: 'translateX(-50%)' }}
                          >
                            W{w}
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                  {/* Phase bars */}
                  {timeline.phases.map((p, i) => {
                    const maxWk = timeline.total_duration_weeks || 1;
                    const colors = ['#4ABFCF', '#0F2040', '#6366F1', '#F59E0B', '#10B981', '#EF4444', '#8B5CF6', '#EC4899'];
                    const barColor = colors[i % colors.length];
                    return (
                      <div key={`pbar-${p.name}`} className="flex items-center mb-1.5">
                        <div className="w-40 shrink-0 text-xs font-medium text-[#0F2040] truncate pr-2">{p.name}</div>
                        <div className="flex-1 relative h-7 bg-slate-50 rounded">
                          {/* Grid lines */}
                          {Array.from({ length: maxWk }, (_, wi) => (
                            <div key={wi} className="absolute top-0 bottom-0 border-l border-slate-100" style={{ left: `${(wi / maxWk) * 100}%` }} />
                          ))}
                          <div
                            className="absolute h-7 rounded flex items-center px-2"
                            style={{
                              left: `${((p.start_week - 1) / maxWk) * 100}%`,
                              width: `${Math.max((p.duration_weeks / maxWk) * 100, 4)}%`,
                              backgroundColor: barColor,
                            }}
                          >
                            <span className="text-[9px] text-white font-medium truncate">{p.duration_weeks}wk</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Resource Summary */}
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Resource Allocation</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="bc-resource-table">
                    <thead><tr className="bg-[#0F2040] text-white text-xs">
                      <th className="text-left px-3 py-2 font-medium">Role</th><th className="text-left px-3 py-2 font-medium">Total Hours</th>
                      <th className="text-left px-3 py-2 font-medium">Peak FTE</th><th className="text-left px-3 py-2 font-medium">Peak Week</th>
                    </tr></thead>
                    <tbody>
                      {Object.entries(timeline.role_summary).sort((a, b) => b[1].total_hours - a[1].total_hours).map(([roleKey, d]) => (
                        <tr key={`resrole-${roleKey}`} className="bg-white odd:bg-slate-50">
                          <td className="px-3 py-2 font-medium text-[#0F2040]">{d.role_label}</td>
                          <td className="px-3 py-2">{d.total_hours.toLocaleString()}h</td>
                          <td className="px-3 py-2">{d.peak_fte}</td>
                          <td className="px-3 py-2">Week {d.peak_week}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-[#0F2040] bg-slate-50 font-semibold text-[#0F2040]">
                        <td className="px-3 py-2">Total</td>
                        <td className="px-3 py-2">{Object.values(timeline.role_summary).reduce((s, d) => s + d.total_hours, 0).toLocaleString()}h</td>
                        <td className="px-3 py-2">{timeline.peak_fte}</td>
                        <td className="px-3 py-2"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* Customer Resource Allocation */}
              {timeline.client_resources && timeline.client_resources.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Customer Resource Allocation</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="bc-client-resource-table">
                      <thead><tr className="bg-[#0F2040] text-white text-xs">
                        <th className="text-left px-3 py-2 font-medium">Role</th><th className="text-left px-3 py-2 font-medium">Active Weeks</th>
                        <th className="text-left px-3 py-2 font-medium">Avg Commitment</th><th className="text-left px-3 py-2 font-medium">Peak Week</th>
                        <th className="text-left px-3 py-2 font-medium">Peak %</th>
                      </tr></thead>
                      <tbody>
                        {timeline.client_resources.map((cr) => (
                          <tr key={`cr-${cr.label}`} className="bg-white odd:bg-slate-50">
                            <td className="px-3 py-2 font-medium text-[#0F2040]">{cr.label}</td>
                            <td className="px-3 py-2">{cr.active_week_ranges || '—'}</td>
                            <td className="px-3 py-2">{cr.avg_commitment}%</td>
                            <td className="px-3 py-2">Week {cr.peak_week}</td>
                            <td className="px-3 py-2">{cr.peak_pct}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Data Migration Risk Callout */}
        {!executiveMode && di && di.modifier !== 1.0 && (
          <Card className={`${di.is_risk ? 'border-rose-200 bg-rose-50/40' : 'border-emerald-200 bg-emerald-50/40'}`} data-testid="bc-data-integrity">
            <CardHeader className="pb-2">
              <CardTitle className={`text-base font-semibold flex items-center gap-2 ${di.is_risk ? 'text-rose-800' : 'text-emerald-800'}`}>
                <Database className={`w-4 h-4 ${di.is_risk ? 'text-rose-500' : 'text-emerald-500'}`} />
                Data Migration Risk Assessment — {di.label} ({di.modifier}x)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className={`text-sm leading-relaxed ${di.is_risk ? 'text-rose-700' : 'text-emerald-700'}`}>
                {di.summary}
              </p>
              <div className="flex gap-4 mt-3 text-xs">
                <div className={`rounded-lg px-3 py-1.5 ${di.is_risk ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}>
                  <span className="font-medium">Migration Effort:</span> {di.dm_effort?.toLocaleString()}h ({di.dm_pct}% of total)
                </div>
                {di.pm_testing_uplift_pct !== 0 && (
                  <div className={`rounded-lg px-3 py-1.5 ${di.is_risk ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}>
                    <span className="font-medium">PM & Testing:</span> {di.pm_testing_uplift_pct > 0 ? '+' : ''}{di.pm_testing_uplift_pct}% each
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* New Development Callout */}
        {!executiveMode && ndItems && ndItems.length > 0 && (
          <Card className="border-violet-200 bg-violet-50/40" data-testid="bc-new-dev">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold flex items-center gap-2 text-violet-800">
                <Database className="w-4 h-4 text-violet-500" />
                New Development Work — {ndItems.length} Item{ndItems.length > 1 ? 's' : ''}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 mb-3">
                {ndItems.map((nd) => (
                  <div key={`nd-${nd.name}`} className="flex items-center justify-between bg-white border border-violet-100 rounded-lg px-3 py-2">
                    <div>
                      <span className="text-sm font-medium text-[#0F2040]">{nd.name}</span>
                      <span className="text-xs text-violet-500 ml-2 capitalize">{nd.size}</span>
                    </div>
                    <div className="text-sm font-semibold text-violet-700">{nd.final_effort}h</div>
                  </div>
                ))}
              </div>
              {ndOverhead && (
                <div className="flex flex-wrap gap-3 text-xs">
                  <div className="rounded-lg px-3 py-1.5 bg-violet-100 text-violet-700"><span className="font-medium">PM:</span> +{ndOverhead.pm_hours}h</div>
                  <div className="rounded-lg px-3 py-1.5 bg-violet-100 text-violet-700"><span className="font-medium">Testing:</span> +{ndOverhead.testing_hours}h</div>
                  <div className="rounded-lg px-3 py-1.5 bg-violet-100 text-violet-700"><span className="font-medium">Training:</span> +{ndOverhead.training_hours}h</div>
                  <div className="rounded-lg px-3 py-1.5 bg-violet-100 text-violet-700"><span className="font-medium">Documentation:</span> +{ndOverhead.documentation_hours}h</div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Risk & Feasibility */}
        {!executiveMode && narrative?.risk_assessment && (
          <Card className="border-red-100" data-testid="bc-risk">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold text-[#0F2040] flex items-center gap-2">
                <Shield className="w-4 h-4 text-red-500" /> Risk & Feasibility Assessment
                <CommentBubble count={commentCounts.risk_analysis} onClick={setActiveComment} section="risk_analysis" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-sm text-slate-700 leading-relaxed space-y-3">
                {narrative.risk_assessment.split('\n\n').filter(Boolean).map((p, i) => <p key={`para-${i}`}>{p}</p>)}
              </div>
              {/* Modifier factors */}
              {est.modifiers && Object.keys(est.modifiers).length > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Complexity & Risk Factors</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {(() => {
                      // Prefer the canonical server-side split when present.
                      // Falls back to defensive client-side filter for older
                      // estimates / endpoints that don't yet expose it.
                      const source = (est.modifier_factors && Object.keys(est.modifier_factors).length > 0)
                        ? est.modifier_factors
                        : Object.fromEntries(
                            Object.entries(est.modifiers).filter(([k, v]) => {
                              if (typeof v !== 'number' || !Number.isFinite(v)) return false;
                              const NON_MULTIPLIER_KEYS = new Set([
                                'planner_actual_weeks',
                                'pm_meetings_per_week',
                                'pm_fte_per_meeting',
                                'project_contingency_pct',
                                'apply_timeline_stretch_tax',
                              ]);
                              if (NON_MULTIPLIER_KEYS.has(k)) return false;
                              return v >= 0.1 && v <= 5.0;
                            })
                          );
                      return Object.entries(source).sort().map(([k, v]) => (
                        <div key={k} className={`rounded-lg px-3 py-2 text-xs border ${v > 1 ? 'bg-red-50 border-red-100 text-red-700' : v < 1 ? 'bg-emerald-50 border-emerald-100 text-emerald-700' : 'bg-slate-50 border-slate-100 text-slate-600'}`}>
                          <span className="font-medium">{k.replace(/_/g, ' ')}</span>
                          <span className="float-right font-bold">{v.toFixed(2)}</span>
                        </div>
                      ));
                    })()}
                    {ndItems && ndItems.length > 0 && (
                      <div className="rounded-lg px-3 py-2 text-xs border bg-violet-50 border-violet-100 text-violet-700" data-testid="bc-pill-new-dev">
                        <span className="font-medium">new development</span>
                        <span className="float-right font-bold">{Math.round(ndItems.reduce((sum, d) => sum + d.final_effort, 0))}h</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Recommendations */}
        {narrative?.recommendations && (
          <Card className="border-violet-100" data-testid="bc-recommendations">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold text-[#0F2040] flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-violet-500" /> Recommendations & Next Steps
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-sm text-slate-700 leading-relaxed space-y-3">
                {narrative.recommendations.split('\n\n').filter(Boolean).map((p, i) => <p key={`para-${i}`}>{p}</p>)}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Component Appendix with Group Subtotals */}
        {!executiveMode && (
        <Card data-testid="bc-components">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-[#0F2040] flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-slate-400" /> Appendix: Component Breakdown
                <CommentBubble count={commentCounts.component_details} onClick={setActiveComment} section="component_details" />
            </CardTitle>
            <p className="text-xs text-slate-500">{components.length} components</p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="bc-component-table">
                <thead><tr className="bg-[#0F2040] text-white">
                  <th className="text-left px-3 py-2 font-medium">Component</th><th className="text-left px-3 py-2 font-medium">Group</th>
                  <th className="text-left px-3 py-2 font-medium">Effort</th><th className="text-left px-3 py-2 font-medium">Cost</th>
                </tr></thead>
                <tbody>
                  {(() => {
                    const rows = [];
                    let currentGroup = null;
                    let groupEffort = 0;
                    let groupCost = 0;
                    let totalEffortSum = 0;
                    let totalCostSum = 0;

                    components.forEach((c, i) => {
                      if (currentGroup !== null && c.group !== currentGroup) {
                        rows.push(
                          <tr key={`sub-${currentGroup}`} className="bg-slate-100 font-semibold text-[#0F2040]">
                            <td className="px-3 py-1.5" colSpan={2}>Subtotal: {currentGroup}</td>
                            <td className="px-3 py-1.5">{hoursStr(groupEffort, { totals: true })}h</td>
                            <td className="px-3 py-1.5">{costStr(groupCost)}</td>
                          </tr>
                        );
                        groupEffort = 0;
                        groupCost = 0;
                      }
                      currentGroup = c.group;
                      groupEffort += c.effort;
                      groupCost += c.cost;
                      totalEffortSum += c.effort;
                      totalCostSum += c.cost;
                      rows.push(
                        <tr key={`comp-${c.name}`} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50/70'}>
                          <td className="px-3 py-1.5 text-[#0F2040]">{c.name}</td>
                          <td className="px-3 py-1.5 text-slate-500">{c.group}</td>
                          <td className="px-3 py-1.5">{hoursStr(c.effort)}h</td>
                          <td className="px-3 py-1.5">{costStr(c.cost)}</td>
                        </tr>
                      );
                    });

                    if (currentGroup !== null) {
                      rows.push(
                        <tr key={`sub-${currentGroup}`} className="bg-slate-100 font-semibold text-[#0F2040]">
                          <td className="px-3 py-1.5" colSpan={2}>Subtotal: {currentGroup}</td>
                          <td className="px-3 py-1.5">{hoursStr(groupEffort, { totals: true })}h</td>
                          <td className="px-3 py-1.5">{costStr(groupCost)}</td>
                        </tr>
                      );
                    }

                    rows.push(
                      <tr key="total" className="bg-[#0F2040] text-white font-bold">
                        <td className="px-3 py-2" colSpan={2}>TOTAL</td>
                        <td className="px-3 py-2">{hoursStr(totalEffortSum, { totals: true })}h</td>
                        <td className="px-3 py-2">{costStr(totalCostSum)}</td>
                      </tr>
                    );

                    return rows;
                  })()}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
        )}

        {/* Approval Dashboard */}
        {approvalData && approvalData.summary.total > 0 && (
          <Card className="border-t-2 border-t-[#4ABFCF]" data-testid="approval-dashboard">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold text-[#0F2040] flex items-center gap-2">
                  <Users className="w-4 h-4 text-[#4ABFCF]" /> Stakeholder Approval Status
                  {approvalData.summary.overdue_count > 0 && (
                    <Badge className="text-[8px] bg-red-100 text-red-700 border-red-200 flex items-center gap-0.5" data-testid="approval-overdue-badge">
                      <AlertTriangle className="w-2.5 h-2.5" /> {approvalData.summary.overdue_count} overdue
                    </Badge>
                  )}
                  {approvalData.summary.earliest_due_date && approvalData.summary.overdue_count === 0 && (
                    <Badge className="text-[8px] bg-slate-100 text-slate-600 border-slate-200 flex items-center gap-0.5" data-testid="approval-deadline-badge">
                      <CalendarDays className="w-2.5 h-2.5" /> Due {new Date(approvalData.summary.earliest_due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </Badge>
                  )}
                </CardTitle>
                <div className="flex items-center gap-2">
                  {approvalData.summary.pending > 0 && (
                    <Button variant="outline" size="sm" onClick={() => sendReminders(null)}
                      disabled={reminding} className="h-6 text-[10px] px-2" data-testid="approval-remind-all-btn">
                      {reminding ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Bell className="w-3 h-3 mr-1" />}
                      Remind All Pending ({approvalData.summary.pending})
                    </Button>
                  )}
                  <button onClick={fetchApproval} className="text-[10px] text-[#4ABFCF] hover:underline flex items-center gap-1" data-testid="approval-refresh">
                    <RefreshCw className="w-2.5 h-2.5" /> Refresh
                  </button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Progress bar */}
              <div className="mb-4">
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="text-slate-500">
                    <span className="font-semibold text-emerald-600">{approvalData.summary.approved}</span> approved
                    {approvalData.summary.changes_requested > 0 && (
                      <> &middot; <span className="font-semibold text-amber-600">{approvalData.summary.changes_requested}</span> changes requested</>
                    )}
                    &middot; <span className="font-semibold text-slate-400">{approvalData.summary.pending}</span> pending
                  </span>
                  <span className="font-semibold text-[#0F2040]">{approvalData.summary.approved}/{approvalData.summary.total}</span>
                </div>
                <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden flex">
                  {approvalData.summary.approved > 0 && (
                    <div className="bg-emerald-500 transition-all" style={{ width: `${(approvalData.summary.approved / approvalData.summary.total) * 100}%` }} />
                  )}
                  {approvalData.summary.changes_requested > 0 && (
                    <div className="bg-amber-400 transition-all" style={{ width: `${(approvalData.summary.changes_requested / approvalData.summary.total) * 100}%` }} />
                  )}
                </div>
              </div>

              {/* Mini Analytics */}
              {estimateAnalytics && estimateAnalytics.kpis.total_sent > 0 && (
                <div className="grid grid-cols-4 gap-2 mb-4" data-testid="approval-mini-analytics">
                  <div className="bg-white rounded-lg border border-slate-200 px-3 py-2 text-center">
                    <p className="text-[9px] text-slate-400 uppercase font-semibold">Approval Rate</p>
                    <p className="text-lg font-bold text-emerald-600">{estimateAnalytics.kpis.approval_rate}%</p>
                  </div>
                  <div className="bg-white rounded-lg border border-slate-200 px-3 py-2 text-center">
                    <p className="text-[9px] text-slate-400 uppercase font-semibold">Avg Response</p>
                    <p className="text-lg font-bold text-[#0F2040]">
                      {estimateAnalytics.kpis.avg_response_hours < 1 ? `${Math.round(estimateAnalytics.kpis.avg_response_hours * 60)}m` :
                       estimateAnalytics.kpis.avg_response_hours < 24 ? `${Math.round(estimateAnalytics.kpis.avg_response_hours)}h` :
                       `${(estimateAnalytics.kpis.avg_response_hours / 24).toFixed(1)}d`}
                    </p>
                  </div>
                  <div className="bg-white rounded-lg border border-slate-200 px-3 py-2 text-center">
                    <p className="text-[9px] text-slate-400 uppercase font-semibold">Responded</p>
                    <p className="text-lg font-bold text-[#0F2040]">
                      {estimateAnalytics.kpis.approved + estimateAnalytics.kpis.changes_requested}
                      <span className="text-xs font-normal text-slate-400">/{estimateAnalytics.kpis.total_sent}</span>
                    </p>
                  </div>
                  <div className="bg-white rounded-lg border border-slate-200 px-3 py-2 text-center">
                    <p className="text-[9px] text-slate-400 uppercase font-semibold">Overdue</p>
                    <p className={`text-lg font-bold ${estimateAnalytics.kpis.overdue > 0 ? 'text-red-600' : 'text-slate-300'}`}>
                      {estimateAnalytics.kpis.overdue}
                    </p>
                  </div>
                </div>
              )}

              {/* Stakeholder cards */}
              <div className="space-y-2">
                {approvalData.entries.map(entry => {
                  const isExpanded = expandedTimelines.has(entry.id);
                  const activityLog = entry.activity_log || [];
                  const toggleTimeline = () => setExpandedTimelines(prev => {
                    const next = new Set(prev);
                    next.has(entry.id) ? next.delete(entry.id) : next.add(entry.id);
                    return next;
                  });
                  return (
                  <div key={entry.id} className={`rounded-lg border transition-colors ${
                    entry.approval_status === 'approved' ? 'bg-emerald-50/50 border-emerald-200/60' :
                    entry.approval_status === 'changes_requested' ? 'bg-amber-50/50 border-amber-200/60' :
                    'bg-slate-50 border-slate-200'
                  }`} data-testid={`approval-entry-${entry.id}`}>
                    <div className="flex items-start gap-3 px-3 py-2.5">
                    {/* Status icon */}
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                      entry.approval_status === 'approved' ? 'bg-emerald-100' :
                      entry.approval_status === 'changes_requested' ? 'bg-amber-100' :
                      'bg-slate-200'
                    }`}>
                      {entry.approval_status === 'approved' && <Check className="w-4 h-4 text-emerald-600" />}
                      {entry.approval_status === 'changes_requested' && <AlertTriangle className="w-4 h-4 text-amber-600" />}
                      {entry.approval_status === 'pending' && <Clock className="w-4 h-4 text-slate-400" />}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-xs font-semibold text-[#0F2040]">{entry.recipient_name || entry.recipient_email}</span>
                        {entry.recipient_role && <Badge variant="outline" className="text-[7px]">{entry.recipient_role}</Badge>}
                        <Badge className={`text-[8px] ${
                          entry.approval_status === 'approved' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
                          entry.approval_status === 'changes_requested' ? 'bg-amber-100 text-amber-700 border-amber-200' :
                          'bg-slate-100 text-slate-500 border-slate-200'
                        }`}>
                          {entry.approval_status === 'approved' ? 'Approved' :
                           entry.approval_status === 'changes_requested' ? 'Changes Requested' : 'Pending'}
                        </Badge>
                        {entry.opened ? (
                          <span className="text-[8px] text-emerald-500 flex items-center gap-0.5"><Eye className="w-2.5 h-2.5" /> Viewed</span>
                        ) : (
                          <span className="text-[8px] text-slate-400 flex items-center gap-0.5"><EyeOff className="w-2.5 h-2.5" /> Not viewed</span>
                        )}
                        {entry.due_date && entry.approval_status === 'pending' && (() => {
                          const ds = getDeadlineStatus(entry.due_date);
                          return ds ? (
                            <span className={`text-[8px] font-medium flex items-center gap-0.5 ${ds.color}`} data-testid={`deadline-status-${entry.id}`}>
                              <CalendarDays className="w-2.5 h-2.5" /> {ds.label}
                            </span>
                          ) : null;
                        })()}
                        {entry.notification_preference === 'never' && entry.approval_status === 'pending' && (
                          <Badge className="text-[7px] bg-red-50 text-red-500 border-red-200" data-testid={`opted-out-${entry.id}`}>
                            Opted out
                          </Badge>
                        )}
                        {entry.notification_preference === 'weekly' && entry.approval_status === 'pending' && (
                          <Badge className="text-[7px] bg-blue-50 text-blue-500 border-blue-200" data-testid={`weekly-pref-${entry.id}`}>
                            Weekly
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <p className="text-[10px] text-slate-400">{entry.recipient_email}</p>
                        {activityLog.length > 0 && (
                          <button onClick={toggleTimeline}
                            className="text-[9px] text-[#4ABFCF] hover:text-[#3AAFBF] flex items-center gap-0.5 font-medium transition-colors"
                            data-testid={`activity-toggle-${entry.id}`}>
                            <Clock className="w-2.5 h-2.5" />
                            Activity ({activityLog.length})
                            <ChevronDown className={`w-2.5 h-2.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                          </button>
                        )}
                      </div>
                      {entry.approval_comment && (
                        <div className="mt-1.5 bg-white rounded px-2.5 py-1.5 border border-slate-200/80">
                          <p className="text-[9px] font-semibold text-slate-500 flex items-center gap-1 mb-0.5">
                            <MessageSquare className="w-2.5 h-2.5" /> Comment
                          </p>
                          <p className="text-xs text-slate-600 leading-relaxed">{entry.approval_comment}</p>
                        </div>
                      )}
                    </div>

                    {/* Timestamp + Remind */}
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <span className="text-[9px] text-slate-400">
                        {entry.approval_responded_at
                          ? new Date(entry.approval_responded_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                          : entry.sent_at
                          ? `Sent ${new Date(entry.sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                          : ''}
                      </span>
                      {entry.approval_status === 'pending' && entry.notification_preference !== 'never' && (
                        <>
                          <button
                            onClick={() => sendReminders([entry.id])}
                            disabled={remindingId === entry.id}
                            className="text-[9px] text-[#4ABFCF] hover:text-[#3AAFBF] flex items-center gap-0.5 font-medium disabled:opacity-50"
                            data-testid={`approval-remind-${entry.id}`}
                          >
                            {remindingId === entry.id ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Bell className="w-2.5 h-2.5" />}
                            Remind
                          </button>
                          {entry.last_reminded_at && (
                            <span className="text-[8px] text-slate-400">
                              Reminded {new Date(entry.last_reminded_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              {entry.reminder_count > 1 && ` (${entry.reminder_count}x)`}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                    </div>

                    {/* Expandable Activity Timeline */}
                    {isExpanded && activityLog.length > 0 && (
                      <div className="px-3 pb-3 pt-0" data-testid={`activity-timeline-${entry.id}`}>
                        <div className="ml-4 pl-6 border-l-2 border-slate-200 space-y-2.5 pt-1">
                          {activityLog.map((evt, idx) => {
                            const evtIcon = evt.type === 'sent' ? <Send className="w-2.5 h-2.5 text-[#4ABFCF]" /> :
                              evt.type === 'opened' ? <Eye className="w-2.5 h-2.5 text-emerald-500" /> :
                              evt.type === 'reminded' ? <Bell className="w-2.5 h-2.5 text-blue-500" /> :
                              evt.type === 'responded' ? <Check className="w-2.5 h-2.5 text-violet-500" /> :
                              <Clock className="w-2.5 h-2.5 text-slate-400" />;
                            const evtColor = evt.type === 'sent' ? 'bg-[#4ABFCF]/10 border-[#4ABFCF]/30' :
                              evt.type === 'opened' ? 'bg-emerald-50 border-emerald-200' :
                              evt.type === 'reminded' ? 'bg-blue-50 border-blue-200' :
                              evt.type === 'responded' ? 'bg-violet-50 border-violet-200' :
                              'bg-slate-50 border-slate-200';
                            return (
                              <div key={evt.timestamp || `evt-${idx}`} className="relative flex items-start gap-2">
                                <div className={`absolute -left-[25px] w-4 h-4 rounded-full border flex items-center justify-center ${evtColor}`}>
                                  {evtIcon}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-[10px] font-medium text-[#0F2040]">{evt.details}</p>
                                  <p className="text-[9px] text-slate-400">
                                    {evt.timestamp ? new Date(evt.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center py-4 text-xs text-slate-400 border-t border-slate-100">
          Generated by Seertech Blueprint Studio — Seertech Solutions &middot; {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} &middot; Confidential
        </div>
      </div>

      {sendDialogOpen && (
        <SendStakeholdersDialog
          estimateId={estimateId}
          estimate={est}
          onClose={() => { setSendDialogOpen(false); fetchApproval(); }}
        />
      )}

      {activeComment && (
        <CommentPanel
          estimateId={estimateId}
          section={activeComment}
          sectionLabel={(SECTIONS.find(s => s.id === activeComment) || {}).label || activeComment}
          onClose={() => { setActiveComment(null); fetchCommentCounts(); }}
        />
      )}

      <ShareLinkDialog
        open={shareLinkOpen}
        onOpenChange={setShareLinkOpen}
        estimateId={estimateId}
        estimateName={est?.estimate_name}
      />
    </div>
  );
}


/**
 * Compares this estimate's blended rate to the user's portfolio average
 * (excluding self) and renders a colour-coded delta chip beneath the
 * Blended Rate KPI tile. Hidden when the benchmark is still loading,
 * the sample size is too small (< 3 peer estimates), or the current
 * estimate has zero effort.
 */
function BlendedRateBenchmarkChip({ estimateBlended, benchmark, currencySymbol = '$' }) {
  if (!benchmark || !benchmark.portfolio_blended_rate || benchmark.sample_size < 3 || !estimateBlended) {
    return null;
  }
  const portfolio = benchmark.portfolio_blended_rate;
  const deltaPct = ((estimateBlended - portfolio) / portfolio) * 100;
  const onPar = Math.abs(deltaPct) <= 0.5;
  const portfolioRounded = Math.round(portfolio).toLocaleString();
  if (onPar) {
    // "On par" reads cleaner without the misleading ↑/↓ + 0.x% theatre.
    return (
      <p className="text-[10px] mt-0.5 leading-tight text-slate-400" data-testid="bc-blended-rate-benchmark">
        On par with portfolio avg ({currencySymbol}{portfolioRounded}/hr)
      </p>
    );
  }
  const sign = deltaPct > 0 ? '↑' : '↓';
  const direction = deltaPct > 0 ? 'above' : 'below';
  const tone = deltaPct > 0 ? 'text-rose-600' : 'text-emerald-600';
  const absPct = Math.abs(deltaPct).toFixed(1);
  return (
    <p className="text-[10px] mt-0.5 leading-tight" data-testid="bc-blended-rate-benchmark">
      <span className={`font-semibold ${tone}`}>{sign} {absPct}%</span>
      <span className="text-slate-400"> {direction} {currencySymbol}{portfolioRounded}/hr avg</span>
    </p>
  );
}

